// src/components/PipelineQueue.jsx
// Cancel button ONLY shows for "queued" tasks — not running
// "canceling" status displayed while worker catches the MongoDB flag
// Stable polling via useRef (no interval restart on state update)

import { useEffect, useRef, useCallback } from "react";
import api from "../api/api";

const D = {
  surface:      "#191c24",
  surfaceUp:    "#1f2230",
  border:       "rgba(255,255,255,0.07)",
  accent:       "#6c7ef8",
  accentSoft:   "rgba(108,126,248,0.10)",
  accentBorder: "rgba(108,126,248,0.22)",
  purple:       "#a37bf8",
  success:      "#34d399",
  successSoft:  "rgba(52,211,153,0.10)",
  warn:         "#f59e0b",
  warnSoft:     "rgba(245,158,11,0.10)",
  danger:       "#f87171",
  dangerSoft:   "rgba(248,113,113,0.10)",
  teal:         "#2dd4bf",
  tealSoft:     "rgba(45,212,191,0.10)",
  textPrimary:  "#f0f2f7",
  textSec:      "#b0b7ce",
  textMuted:    "#6b7390",
  fontDisplay:  "'Plus Jakarta Sans','DM Sans',sans-serif",
  fontBody:     "'DM Sans',sans-serif",
  fontMono:     "'JetBrains Mono',monospace",
};

const POLL_MS = 5000;
const MAX_POLL = 3600; // 10 min hard cutoff

// All terminal states — polling stops for these
const DONE_STATUSES = ["complete", "failed", "canceled"];

const STATUS_CONFIG = {
  queued:    { color: D.warn,    bg: D.warnSoft,    label: "QUEUED",     icon: "⏳" },
  running:   { color: D.accent,  bg: D.accentSoft,  label: "RUNNING",    icon: null },
  canceling: { color: D.teal,    bg: D.tealSoft,    label: "CANCELING",  icon: "⏹" },
  complete:  { color: D.success, bg: D.successSoft, label: "COMPLETE",   icon: "✅" },
  failed:    { color: D.danger,  bg: D.dangerSoft,  label: "FAILED",     icon: "❌" },
  canceled:  { color: D.textMuted, bg: "rgba(107,115,144,0.1)", label: "CANCELED", icon: "🚫" },
};

// Safe user-facing error messages — never show raw backend strings
const SAFE_ERRORS = {
  "QuotaError":     "Generation limit reached.",
  "ValueError":     "Content validation failed — quota refunded.",
  "TimeoutError":   "Job timed out — quota refunded.",
  "RuntimeError":   "An internal error occurred — quota refunded.",
};
function sanitizeError(raw) {
  if (!raw) return "Something went wrong — quota refunded.";
  for (const [key, msg] of Object.entries(SAFE_ERRORS)) {
    if (raw.includes(key)) return msg;
  }
  // Don't surface raw exception strings — they may contain credentials/paths
  return "Something went wrong — quota refunded.";
}

function Spinner({ color = D.accent, size = 14 }) {
  return (
    <span style={{ display: "inline-block", position: "relative", width: size, height: size, flexShrink: 0 }}>
      {/* outer ring */}
      <span style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid rgba(108,126,248,0.15)`, borderTopColor: color, animation: "queueOrbit 0.85s cubic-bezier(0.4,0,0.6,1) infinite" }} />
      {/* inner ring */}
      <span style={{ position: "absolute", inset: 3, borderRadius: "50%", border: `1.5px solid rgba(108,126,248,0.1)`, borderTopColor: D.purple, animation: "queueOrbitRev 0.6s cubic-bezier(0.4,0,0.6,1) infinite" }} />
    </span>
  );
}

function ProgressBar({ pct = 0, color = D.accent }) {
  const w = Math.min(100, Math.max(5, pct));
  return (
    <div style={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,0.05)", overflow: "hidden", marginTop: 7, position: "relative" }}>
      <div style={{
        height: "100%", width: `${w}%`,
        background: `linear-gradient(90deg, ${color} 0%, ${D.purple} 60%, ${color} 100%)`,
        backgroundSize: "200% 100%",
        borderRadius: 99,
        transition: "width 0.9s cubic-bezier(0.22,1,0.36,1)",
        animation: "queueShimmer 2.4s ease infinite, queueBarGlow 2s ease infinite",
      }} />
    </div>
  );
}

const STALE_MINUTES = 25; // pipeline normally finishes in < 10 min

function isTaskStale(task) {
  if (!task.created_at || task.status !== "running") return false;
  const ageMs = Date.now() - new Date(task.created_at).getTime();
  return ageMs > STALE_MINUTES * 60 * 1000;
}

function TaskRow({ task, onCancel }) {
  const cfg      = STATUS_CONFIG[task.status] || STATUS_CONFIG.queued;
  const isActive = !DONE_STATUSES.includes(task.status);
  const stale    = isTaskStale(task);

  const statusLabel =
      task.status === "queued"    ? "Waiting in queue…"
    : task.status === "running"   ? (task.step_label || "Processing…")
    : task.status === "canceling" ? "Canceling — stopping at next checkpoint…"
    : task.status === "complete"  ? "Done — content ready below ↓"
    : task.status === "failed"    ? sanitizeError(task.error)
    : task.status === "canceled"  ? "Canceled — quota refunded"
    : task.status;

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "13px 16px", background: D.surfaceUp, borderRadius: 12,
      border: `1px solid ${isActive ? cfg.color + "35" : D.border}`,
      marginBottom: 8,
      animation: "queueSlideIn 0.38s cubic-bezier(0.22,1,0.36,1) both",
      transition: "border-color 0.35s ease, box-shadow 0.35s ease, transform 0.25s ease",
      boxShadow: isActive ? `0 0 0 1px ${cfg.color}18, 0 4px 20px rgba(0,0,0,0.15)` : "none",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      {/* Icon */}
      <div style={{
        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
        background: cfg.bg, display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 14, marginTop: 1,
        border: `1px solid ${cfg.color}25`,
        transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        animation: isActive ? `queueGlow 2.5s ease infinite` : "none",
      }}>
        {(task.status === "running" || task.status === "canceling")
          ? <Spinner color={cfg.color} size={14} />
          : <span>{cfg.icon}</span>}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
          <span style={{
            fontFamily: D.fontBody, fontWeight: 600, fontSize: 13, color: D.textPrimary,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260,
          }}>
            {task.topic}
          </span>
          {task.duplicate && (
            <span style={{
              fontFamily: D.fontMono, fontSize: 9.5, fontWeight: 600, color: D.warn,
              background: D.warnSoft, border: `1px solid ${D.warn}30`,
              padding: "1px 6px", borderRadius: 4,
            }}>
              ALREADY RUNNING
            </span>
          )}
        </div>
        <span style={{
          fontFamily: D.fontMono, fontSize: 11,
          color: task.status === "failed" ? D.danger
               : task.status === "canceling" ? D.teal
               : D.textMuted,
        }}>
          {statusLabel}
        </span>
        {task.status === "running" && <ProgressBar pct={task.pct || 0} color={cfg.color} />}
        {stale && (
          <div style={{
            marginTop: 8, padding: "8px 12px", borderRadius: 8,
            background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)",
            fontFamily: D.fontMono, fontSize: 10.5, color: D.warn, lineHeight: 1.5,
          }}>
            Taking longer than usual. Your content may already be ready —{" "}
            <a href="/dashboard" style={{ color: D.accent, textDecoration: "underline" }}>
              check Content Archive
            </a>
            {" "}or{" "}
            <a href="/history" style={{ color: D.accent, textDecoration: "underline" }}>
              Content History
            </a>.
          </div>
        )}
      </div>

      {/* Badge + cancel (ONLY for queued) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <span style={{
          fontFamily: D.fontMono, fontSize: 10, fontWeight: 700,
          color: cfg.color, background: cfg.bg,
          border: `1px solid ${cfg.color}30`,
          padding: "3px 8px", borderRadius: 5, letterSpacing: "0.05em",
        }}>
          {cfg.label}
        </span>

        {/* Cancel button: ONLY shown when status === "queued" */}
        {task.status === "queued" && onCancel && (
          <button
            onClick={() => onCancel(task.task_id)}
            title="Cancel and refund quota"
            style={{
              width: 24, height: 24, borderRadius: 6, flexShrink: 0,
              background: "transparent", border: `1px solid rgba(248,113,113,0.2)`,
              color: D.danger, cursor: "pointer", fontSize: 11,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = D.dangerSoft}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function PipelineQueue({ tasks, setTasks, onTaskComplete, onTaskFailed }) {
  const pollCountRef = useRef({});
  // Tracks which task IDs have already had onTaskComplete fired — prevents
  // double-firing when a task transitions running→complete via pollTask AND
  // the already-complete useEffect below both see the completed status.
  const firedRef = useRef(new Set());

  // Keep tasks accessible in interval closure without restarting the interval
  const tasksRef = useRef(tasks);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const pollTask = useCallback(async (task) => {
    const count = (pollCountRef.current[task.task_id] || 0) + 1;
    pollCountRef.current[task.task_id] = count;

    // Hard cutoff — if a job runs for 10+ min something is badly wrong
    if (count > MAX_POLL) {
      const msg = "Job timed out — please try again.";
      setTasks(prev => prev.map(t =>
        t.task_id === task.task_id ? { ...t, status: "failed", error: msg } : t
      ));
      onTaskFailed?.(task, msg);
      delete pollCountRef.current[task.task_id];
      return;
    }

    try {
      const { data } = await api.get(`/pipeline/task/${task.task_id}`);

      setTasks(prev => prev.map(t =>
        t.task_id === task.task_id
          ? {
              ...t,
              status:     data.status,
              step:       data.step       ?? null,
              step_label: data.step_label ?? null,
              pct:        data.pct        ?? (data.status === "queued" ? 5 : t.pct),
              error:      data.error      ?? null,
              // cancellable comes from backend — only true for "queued"
              cancellable: data.cancellable ?? false,
            }
          : t
      ));

      if (data.status === "complete") {
        firedRef.current.add(task.task_id);
        delete pollCountRef.current[task.task_id];
        onTaskComplete?.(data.result);
      } else if (data.status === "failed") {
        delete pollCountRef.current[task.task_id];
        onTaskFailed?.(task, sanitizeError(data.error));
      } else if (data.status === "canceled") {
        delete pollCountRef.current[task.task_id];
        // No error banner for cancel — user initiated it
      }
    } catch (e) {
      console.warn(`[Queue] Poll error ${task.task_id}:`, e.message);
    }
  }, [setTasks, onTaskComplete, onTaskFailed]);

  // When tasks are loaded from DB on re-login they may already be "complete".
  // PipelineQueue won't poll them (DONE_STATUSES), so onTaskComplete would
  // never fire. Only fire for tasks completed within the last 4 hours —
  // that covers the "logged out mid-run, came back" scenario without
  // resurrecting old runs from days ago onto the homepage.
  useEffect(() => {
    const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
    const now = Date.now();
    const restored = tasks.filter(t => {
      if (t.status !== "complete") return false;
      if (firedRef.current.has(t.task_id)) return false;
      if (!t.created_at) return false;
      return now - new Date(t.created_at).getTime() < FOUR_HOURS_MS;
    });
    if (restored.length === 0) return;
    restored.forEach(async (task) => {
      firedRef.current.add(task.task_id);
      try {
        const { data } = await api.get(`/pipeline/task/${task.task_id}`);
        if (data.status === "complete") {
          onTaskComplete?.(data.result);
        }
      } catch (e) {
        console.warn(`[Queue] Could not fetch result for restored task ${task.task_id}:`, e.message);
      }
    });
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable interval — only restarts if pollTask changes (it doesn't)
  useEffect(() => {
    const interval = setInterval(() => {
      const active = tasksRef.current.filter(t => !DONE_STATUSES.includes(t.status));
      if (active.length === 0) return;
      active.forEach(t => pollTask(t));
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [pollTask]);

  const handleCancel = async (taskId) => {
    // Optimistically update UI immediately
    setTasks(prev => prev.map(t =>
      t.task_id === taskId
        ? { ...t,
            // queued → canceled immediately (it's removed from RQ)
            // running → canceling (worker needs a few seconds to catch the flag)
            status: t.status === "queued" ? "canceled" : "canceling",
            cancellable: false }
        : t
    ));
    try {
      await api.post(`/pipeline/task/${taskId}/cancel`);
    } catch (e) {
      // Revert optimistic update on error
      console.error("[Queue] Cancel failed:", e.message);
      setTasks(prev => prev.map(t =>
        t.task_id === taskId && t.status === "canceling"
          ? { ...t, status: "running", cancellable: false }
          : t
      ));
    }
  };

  if (tasks.length === 0) return null;

  const activeCount = tasks.filter(t => !DONE_STATUSES.includes(t.status)).length;
  const doneCount   = tasks.filter(t => t.status === "complete").length;
  const failCount   = tasks.filter(t => t.status === "failed").length;
  const allDone     = activeCount === 0;

  // Only cancel queued tasks in bulk — never send cancel to running tasks from here
  const cancelableIds = tasks
    .filter(t => t.status === "queued")
    .map(t => t.task_id);

  return (
    <>
      <style>{`
        @keyframes queueSpin      { to { transform: rotate(360deg); } }
        @keyframes queueFadeUp    { from { opacity:0; transform:translateY(14px); filter:blur(4px); } to { opacity:1; transform:translateY(0); filter:blur(0); } }
        @keyframes queueSlideIn   { from { opacity:0; transform:translateX(-18px); filter:blur(3px); } to { opacity:1; transform:translateX(0); filter:blur(0); } }
        @keyframes queuePulse     { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes queueShimmer   { 0%{ background-position:-400% center; } 100%{ background-position:400% center; } }
        @keyframes queueGlow      { 0%,100%{ box-shadow:0 0 0 0 rgba(108,126,248,0.35); } 60%{ box-shadow:0 0 0 8px rgba(108,126,248,0); } }
        @keyframes queueOrbit     { to { transform: rotate(360deg); } }
        @keyframes queueOrbitRev  { to { transform: rotate(-360deg); } }
        @keyframes queueDotPulse  { 0%,80%,100%{ transform:scale(0.5); opacity:0.25; } 40%{ transform:scale(1); opacity:1; } }
        @keyframes queueBarGlow   { 0%,100%{ box-shadow:0 0 4px rgba(108,126,248,0.4); } 50%{ box-shadow:0 0 14px rgba(108,126,248,0.7), 0 0 25px rgba(163,123,248,0.3); } }
      `}</style>

      <div style={{
        background: D.surface,
        border: `1px solid ${allDone ? D.border : D.accentBorder}`,
        borderRadius: 16, overflow: "hidden", marginBottom: 24,
        boxShadow: allDone
          ? "0 4px 20px rgba(0,0,0,0.2)"
          : `0 0 0 1px ${D.accentBorder}, 0 8px 40px rgba(108,126,248,0.1), 0 4px 20px rgba(0,0,0,0.25)`,
        animation: "queueFadeUp 0.42s cubic-bezier(0.22,1,0.36,1) both",
        transition: "border-color 0.45s ease, box-shadow 0.45s ease",
      }}>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px", borderBottom: `1px solid ${D.border}`,
          background: "rgba(255,255,255,0.015)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!allDone && (
              <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ position: "absolute", width: 18, height: 18, borderRadius: "50%", background: `${D.accent}22`, animation: "queueGlow 1.6s ease infinite" }} />
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: D.accent, display: "inline-block", animation: "queuePulse 1.4s ease infinite", position: "relative" }} />
              </span>
            )}
            <span style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 13.5, color: D.textPrimary }}>
              {allDone ? "Pipeline Complete"
                       : `Processing ${activeCount} topic${activeCount !== 1 ? "s" : ""}…`}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              {doneCount  > 0 && <span style={{ fontFamily: D.fontMono, fontSize: 10, fontWeight: 600, color: D.success,   background: D.successSoft, border: `1px solid ${D.success}30`,  padding: "2px 7px", borderRadius: 4 }}>{doneCount} done</span>}
              {failCount  > 0 && <span style={{ fontFamily: D.fontMono, fontSize: 10, fontWeight: 600, color: D.danger,    background: D.dangerSoft,  border: `1px solid ${D.danger}30`,   padding: "2px 7px", borderRadius: 4 }}>{failCount} failed</span>}
              {activeCount > 0 && <span style={{ fontFamily: D.fontMono, fontSize: 10, fontWeight: 600, color: D.accent,   background: D.accentSoft,  border: `1px solid ${D.accentBorder}`, padding: "2px 7px", borderRadius: 4 }}>{activeCount} active</span>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            {/* Cancel All only cancels QUEUED tasks */}
            {cancelableIds.length > 0 && (
              <button
                onClick={() => cancelableIds.forEach(id => handleCancel(id))}
                style={{ padding: "5px 12px", borderRadius: 7, cursor: "pointer", background: D.dangerSoft, border: `1px solid rgba(248,113,113,0.25)`, color: D.danger, fontFamily: D.fontBody, fontSize: 12, fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(248,113,113,0.18)"}
                onMouseLeave={e => e.currentTarget.style.background = D.dangerSoft}
              >
                Cancel Queued
              </button>
            )}
            {allDone && (
              <button
                onClick={() => setTasks([])}
                style={{ padding: "5px 12px", borderRadius: 7, cursor: "pointer", background: "rgba(255,255,255,0.04)", border: `1px solid ${D.border}`, color: D.textMuted, fontFamily: D.fontBody, fontSize: 12, fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.color = D.textPrimary}
                onMouseLeave={e => e.currentTarget.style.color = D.textMuted}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Overall progress bar */}
        {!allDone && tasks.length > 0 && (
          <div style={{ height: 3, background: "rgba(255,255,255,0.04)", overflow: "hidden", position: "relative" }}>
            <div style={{
              height: "100%", width: `${(doneCount / tasks.length) * 100}%`,
              background: `linear-gradient(90deg, ${D.accent} 0%, ${D.purple} 50%, ${D.accent} 100%)`,
              backgroundSize: "200% 100%",
              transition: "width 1s cubic-bezier(0.22,1,0.36,1)",
              animation: "queueShimmer 2.5s ease infinite",
            }} />
          </div>
        )}

        {/* Task rows */}
        <div style={{ padding: "12px 14px 6px" }}>
          {tasks.map(task => (
            <TaskRow key={task.task_id} task={task} onCancel={handleCancel} />
          ))}
        </div>

        {/* Footer */}
        {!allDone && (
          <div style={{ padding: "6px 18px 12px", borderTop: `1px solid ${D.border}`, fontFamily: D.fontMono, fontSize: 10.5, color: D.textMuted }}>
            ↻ Auto-refreshing every 5s · Safe to navigate away — jobs keep running
          </div>
        )}
      </div>
    </>
  );
}